import { GridCallbackDetails } from '@mui/x-data-grid/models/api/gridCallbackDetails'
import type { GridRowParams } from '@mui/x-data-grid/models/params'
import {
  GridRowEditStartParams,
  GridRowEditStopParams,
} from '@mui/x-data-grid/models/params/gridRowParams'
import { MuiBaseEvent } from '@mui/x-data-grid-premium'
import {
  DataGridPro,
  DataGridProProps,
  GridCellParams,
  GridColDef,
  GridColumnMenuHideItem,
  GridColumnMenuManageItem,
  GridColumnMenuProps,
  GridColumnMenuSortItem,
  GridColumnVisibilityModel,
  GridRowHeightReturnValue,
  GridValidRowModel,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { frFR } from '@mui/x-data-grid-pro/locales'
import { styleConstants } from '@paywithextend/components'
import {
  base_colorContentBorder,
  base_colorRaisedSurface,
  table_tableRowHover,
  table_tableRowSelected,
  table_tableRowSelectedHover,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_size_md,
  font_size_sm,
} from 'ext-ui/style/typography'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { renderCell } from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import { renderHeader } from 'src/components/DataGrid/cells/DataGridHeaderCell/DataGridHeaderCell'
import useColumnsState from 'src/components/DataGrid/hooks/useColumnsState.hook'
import useTransferVerticalScroll from 'src/components/DataGrid/hooks/useTransferVerticalScroll.hook'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { ExtendLocales } from 'src/enums'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectBrandColorFontPrimary } from 'src/redux/selectors/session.selectors'
import { InfiniteScrollProps } from 'src/types'
import { css } from 'styled-components'

export enum DataGridHeaderStyle {
  Default,
  NoBackground,
  /**
   * The card-management treatment (EX-40736): an uppercase kicker row over a
   * single hairline, no filled band, no column dividers, and body cells at
   * 14px/18px regular.
   *
   * A superset of `NoBackground`, and the reason it is an enum member rather
   * than another `sx` block at each call site: `CardBudgetsTable`,
   * `CardVirtualCardsTable` and `CardOverviewRecentTransactions` each carry a
   * verbatim copy of these ~40 lines, each with the same warning that this
   * grid merges consumer `sx` with a SHALLOW spread and every copied key has to
   * restate the rules it replaces. Three copies is where that stopped being
   * tolerable; the Transactions tab would have been the fourth.
   *
   * Two things this can do that a consumer's `sx` cannot:
   *  - reach the header title at all. A consumer restyling MUI's own
   *    `.MuiDataGrid-columnHeaderTitle` from outside is in a cascade race it
   *    loses intermittently, which is why all three tables supply their own
   *    `renderHeader` instead; from in here it is the same emotion channel, so
   *    it wins. More importantly this is the only place that knows about
   *    `.headerName` — the class on the shared `DataGridHeaderCell` this grid
   *    installs on every column by default, and therefore the element that
   *    actually carries the title on nearly every table in the app.
   *  - extend the shared rules rather than replace them, since it composes
   *    before the shallow merge rather than after it.
   */
  Kicker,
}

/**
 * The bottom inset `DataGridHeaderStyle.Kicker` puts on the grid root.
 *
 * Exported because a caller that clamps its own height to its content has to
 * add this back, or the clamp eats the padding and clips the last row into it.
 * One definition, consumed by both sides — the number below and the CSS that
 * applies it must not drift.
 */
export const DATA_GRID_KICKER_BOTTOM_INSET = 24

/** Shared numeric header height passed to MUI's DataGridPro. */
export const DATA_GRID_HEADER_HEIGHT = 44

/**
 * Grid chrome MUI X's height model does not know about, which a caller sizing
 * itself to its rows has to add back.
 *
 * Two pixels, from two independent places, and BOTH are ours rather than MUI's:
 *
 *  1. The `borderTop` this grid puts on the DataGridPro root below. The
 *     virtualizer measures the root through a `ResizeObserver` and reads
 *     `entry.contentRect` (`@mui/x-virtualizer/features/dimensions.js`), which
 *     is the CONTENT box — borders are excluded. So the viewport it computes is
 *     1px shorter than the box the caller asked for. Note this applies even
 *     under `NoBackground`/`Kicker`, where the border is `transparent`: a
 *     transparent border still occupies layout.
 *  2. The 1px `borderBottom` those header variants draw under
 *     `.MuiDataGrid-columnHeaders`. MUI derives `headersTotalHeight` from the
 *     `columnHeaderHeight` PROP rather than measuring the element, and
 *     `.MuiDataGrid-columnHeaders` has no height of its own — it is sized by
 *     its 44px header row plus that hairline, so it renders at 45.
 *
 * Together they leave the rows 2px more space than they get, and MUI decides
 * `hasScrollY` on `contentHeight > viewportHeight` — so a table sized to
 * exactly fit its rows overflows by 2px and paints a full-height vertical
 * scrollbar over rows that have nowhere to scroll.
 *
 * Note that scrollbar is `.MuiDataGrid-scrollbar--vertical`, a separate
 * absolutely-positioned overlay element in MUI X v8 — NOT the virtual
 * scroller. Forcing `overflowY: 'hidden'` on `.MuiDataGrid-virtualScroller`,
 * which the fixed-height tables do, never touched it.
 */
export const DATA_GRID_UNMEASURED_BORDER_HEIGHT = 2

/**
 * Inset the shared wrapper subtracts from a consumer-provided page height.
 * Consumers measuring the grid viewport add this back before passing `height`.
 */
export const DATA_GRID_HEIGHT_OFFSET = 60

export const DataGridRowHeight = {
  Default: 52,
  Comfy: 64,
}

interface DataGridProps<T, R extends GridValidRowModel>
  extends Omit<
    DataGridProProps<R>,
    'localeText' | 'loading' | 'sortingMode' | 'onRowsScrollEnd'
  > {
  rowBuffer?: number
  columnBuffer?: number
  disableColumnVirtualization?: boolean
  /**
   * Drops the column-visibility items ("Hide column" and "Manage columns") from
   * the column-header menu, leaving a sort-only menu. Use when a table has a
   * fixed column set where hiding isn't supported (column visibility is
   * controlled with no change handler, so hiding would silently revert).
   * Defaults to false, so the menu is unchanged for every table that doesn't
   * opt in.
   */
  disableColumnHiding?: boolean
  height?: number
  rowHeight?: number
  infiniteScrollProps?: InfiniteScrollProps<T>
  filtersBody: T
  /**
   * Always includes `__check__`, `chevron`, and the first column by default.
   */
  alwaysVisibleColumns?: string[]
  headerStyle?: DataGridHeaderStyle
  /**
   * Follows the same format as `initialState` in `DataGridProProps`
   * However, `__check__` and the first column are always pinned left, and `chevron` is always pinned right
   * so you do not need to include them in the `pinnedColumns` property.
   */
  initialState?: DataGridProProps<R>['initialState']
  /**
   * When false, suppresses automatically pinning the first column on the left.
   */
  pinFirstColumn?: boolean
  /**
   * Optional ordered column fields override.
   */
  orderedColumnFields?: string[]
  /**
   * Registers a callback that resets column order back to the default order.
   */
  onRegisterResetColumnOrder?: (reset: () => void) => void
  /** Called when the user changes column order via drag/drop. */
  onUserColumnOrderChange?: () => void
  // any callbacks that use types from `GridEventListener` have no knowledge of the row model type
  // so we are manually defining them here with the generic we forward alone
  onRowEditStart?: (
    params: GridRowEditStartParams<R>,
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    details: GridCallbackDetails,
  ) => void
  onRowEditStop?: (
    params: GridRowEditStopParams<R>,
    event: MuiBaseEvent,
    details: GridCallbackDetails,
  ) => void
  onRowClick?: (
    params: GridRowParams<R>,
    event: React.MouseEvent<HTMLElement>,
    details: GridCallbackDetails,
  ) => void
  // custom event implementations from https://mui.com/x/react-data-grid/events/#catalog-of-events that don't have prop event handlers
  // i.e. onHeaderSelectionCheckboxChange?: GridEventListener<'headerSelectionCheckboxChange'>
}

export const handleCellClick = (
  _: GridCellParams,
  event: MuiEvent<React.MouseEvent>,
): void => {
  const target = event.target as HTMLElement
  if (event.metaKey || event.ctrlKey || target.closest('a')) {
    event.stopPropagation()
    return
  }
}

const DataGrid = <T, R extends GridValidRowModel>(
  props: DataGridProps<T, R>,
): React.ReactElement => {
  const brandColor = useSelector(selectBrandColorFontPrimary)
  const locale = useSelector(selectNullSafeLocaleFromState)

  const handleOnRowsScrollEnd = useCallback<
    NonNullable<DataGridProProps['onRowsScrollEnd']>
  >((): void => {
    if (
      !props?.infiniteScrollProps?.loading &&
      !props?.infiniteScrollProps?.complete
    ) {
      const page =
        props?.infiniteScrollProps?.page !== undefined
          ? props?.infiniteScrollProps?.page + 1
          : undefined
      const filtersBody =
        props.filtersBody !== undefined ? props.filtersBody : undefined
      props?.infiniteScrollProps?.fetch(filtersBody, page)
    }
  }, [
    props.filtersBody,
    props?.infiniteScrollProps?.fetch,
    props?.infiniteScrollProps?.loading,
    props?.infiniteScrollProps?.complete,
    props?.infiniteScrollProps?.page,
  ])

  const headerBgColor = useMemo(() => {
    // Kicker is NoBackground plus a typographic treatment — the band is
    // unfilled in both.
    switch (props.headerStyle) {
      case DataGridHeaderStyle.NoBackground:
      case DataGridHeaderStyle.Kicker:
        return styleConstants.colors.white
      case DataGridHeaderStyle.Default:
      default:
        return styleConstants.colors.gray_lightest
    }
  }, [props.headerStyle])

  const headerTopBorderColor = useMemo(() => {
    switch (props.headerStyle) {
      case DataGridHeaderStyle.NoBackground:
      case DataGridHeaderStyle.Kicker:
        return 'transparent'
      case DataGridHeaderStyle.Default:
      default:
        return base_colorContentBorder
    }
  }, [props.headerStyle])

  /**
   * The Kicker variant's additions, merged into this grid's own `sx` below —
   * before the consumer's, so a call site can still override any of it.
   *
   * Everything here is lifted verbatim in intent from the three card-management
   * tables that each hand-rolled it; see `DataGridHeaderStyle.Kicker`.
   */
  const kickerSx = useMemo(
    () =>
      props.headerStyle === DataGridHeaderStyle.Kicker
        ? {
            // The design has no column dividers, and with resize/reorder off
            // the separators the shared grid keeps at `opacity: 1` have nothing
            // to grab — they only read as rules between columns.
            '& .MuiDataGrid-columnSeparator': { display: 'none' },
            // The shared grid insets headers by 16px while leaving cells at 0,
            // so every header sits a notch right of the column it labels.
            /*
              Headers inset to match their CONTENT, not to zero.

              Cells render through `DataGridCellWrapper`, which applies its own
              `0 16px`. Zeroing the header padding therefore does not align the
              two — it pushes every label 16px LEFT of the column it labels.
              The two insets have to be the same number, and 16px is the one the
              cells already use.

              A table that renders plain cells instead of `DataGridCell` (the
              three card-management tables do) has no wrapper inset, so it pairs
              this variant with its own `0` on both.

              Do NOT widen this to the design's 24px. The prototype measures 24,
              but it lays out with CSS grid over a fixed column template; this
              grid distributes `flex` widths over whatever is left after padding,
              so the extra 8px per side came straight out of the cells and
              started truncating "Receipts", "Amount" and the date.
            */
            '& .MuiDataGrid-columnHeader': {
              padding: `0 ${spacing(4)}`,
              backgroundColor: base_colorRaisedSurface,
              borderLeftStyle: 'unset',
            },
            /*
              The vertical rules between columns. Not the column SEPARATORS
              above — these are the pinned-section borders the shared grid draws
              at `opacity: 1` so a pinned column reads as frozen while the rest
              scrolls. The design has no dividers, and on a six-column table
              that never scrolls they are pure noise.
            */
            '& .MuiDataGrid-columnHeader--withRightBorder, & .MuiDataGrid-columnHeader--withLeftBorder, & .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-cell--withLeftBorder':
              {
                borderRight: 'none',
                borderLeft: 'none',
              },
            // One hairline under the whole header row. The shared grid marks
            // the band with a background fill instead, which this variant
            // removes.
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: `1px solid ${base_colorContentBorder}`,
            },
            /*
              The uppercase kicker itself. Styled here rather than through a
              consumer `renderHeader` — see the note on the enum member — and
              uppercased in CSS, never in the translation source, so the
              accessible name MUI reads from `headerName` stays the plain
              phrase.

              BOTH selectors are required, and `.headerName` is the one that
              does the work on nearly every table here. This grid gives every
              column that does not bring its own `renderHeader` the shared
              `DataGridHeaderCell`, which emits `<div className="headerName">`
              — so `.MuiDataGrid-columnHeaderTitle`, the element MUI renders
              for its OWN default title, never exists on those tables and a
              rule targeting it alone silently matches nothing. The MUI
              selector stays for a column that opts out with a bare
              `renderHeader`.
            */
            '& .MuiDataGrid-columnHeader .headerName, & .MuiDataGrid-columnHeaderTitle':
              {
                fontSize: font_size_sm,
                fontFamily: font_family_weight_medium,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: text_colorTextSecondary,
              },
            /*
              No scroll-edge shadows. The shared grid paints a 10px gradient at
              whichever edge still has content to scroll to, and toggles it from
              JS via `data-left-fade` / `data-right-fade`. On a six-column table
              that fits, sub-pixel width fuzz is enough to leave the right one
              on, which reads as a shadow down the side of the Amount column.

              `content: none` rather than `opacity: 0` because opacity is
              exactly what that JS drives — it would switch the shadow back on
              at the next scroll measurement.
            */
            '& .MuiDataGrid-main::before, & .MuiDataGrid-main::after': {
              content: 'none',
            },
            /*
              Space under the last row, matching the inset the sibling card
              tables get from their panel padding. Without it the final row sits
              flat against the card's bottom edge and the table reads as
              cropped. On the grid ROOT rather than the scroller so it is part
              of the box the fixed height measures, not extra scrollable range.
            */
            paddingBottom: `${DATA_GRID_KICKER_BOTTOM_INSET}px`,
            /*
              The scrollbar gutter's filler carries a left border, which reads
              as a stray rule floating past the last column — the same thing
              fixed on the Overview tables.

              All four filler variants, not just the base class: MUI splits the
              gutter into `--header`, `--borderTop` and `--pinnedRight` pieces,
              and styling only `.MuiDataGrid-scrollbarFiller` leaves whichever
              of them is rendered still drawing its rule.
            */
            '& .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-scrollbarFiller--header, & .MuiDataGrid-scrollbarFiller--borderTop, & .MuiDataGrid-scrollbarFiller--pinnedRight, & .MuiDataGrid-filler':
              {
                borderLeft: 'none',
                borderTop: 'none',
              },
            '& .MuiDataGrid-cell': {
              // Restated from the shared rule this key replaces — the merge
              // below is a shallow spread, so these go with it or every cell
              // loses its flex centring.
              display: 'flex',
              alignItems: 'center',
              // Zero here; `DataGridCellWrapper` supplies the 16px that matches
              // the header inset above.
              padding: 0,
              fontSize: font_size_md,
              fontFamily: font_family_weight_regular,
              color: text_colorTextPrimary,
              // NOT optional. Cells that render plain elements rather than the
              // grid's own `DataGridCell` otherwise inherit the page's much
              // taller line box, which drops a name in `UserAvatarAndName`
              // below its avatar.
              lineHeight: '18px',
            },
          }
        : {},
    [props.headerStyle],
  )

  const loadingImage = (): React.ReactElement => <LoadingBackgroundPage />

  const customColumnMenu = (
    menuProps: GridColumnMenuProps,
  ): React.ReactElement => {
    const itemProps = {
      colDef: menuProps.colDef,
      onClick: menuProps.hideMenu,
    }

    return (
      <div>
        <GridColumnMenuSortItem
          sx={{
            fontSize: 14,
            lineHeight: 18,
            color: brandColor,
            fontFamily: 'unset',
          }}
          {...itemProps}
        />
        {/*
          Both items are column-visibility affordances (Hide column hides one;
          Manage columns is the show/hide panel). `disableColumnHiding` drops
          both, leaving a sort-only menu, for tables with a fixed column set
          where hiding isn't supported.
        */}
        {!props.disableColumnHiding && (
          <>
            <GridColumnMenuHideItem
              {...itemProps}
              sx={{
                fontSize: 14,
                lineHeight: 18,
                color: brandColor,
                fontFamily: styleConstants.fontFamilies.regular,
              }}
            />
            <GridColumnMenuManageItem
              sx={{
                fontSize: 14,
                lineHeight: 18,
                color: brandColor,
                fontFamily: styleConstants.fontFamilies.regular,
              }}
              {...itemProps}
            />
          </>
        )}
      </div>
    )
  }

  const {
    columns,
    getRowHeight,
    slots,
    slotProps,
    sx,
    columnVisibilityModel,
    apiRef: propApiRef,
    initialState,
    disableRowSelectionOnClick = true,
    editMode = 'row',
    pinFirstColumn = true,
    orderedColumnFields,
    onRegisterResetColumnOrder,
    onUserColumnOrderChange,
    // Consumed by customColumnMenu (via `props`); destructured here only so it
    // isn't forwarded to DataGridPro as an unknown prop.
    disableColumnHiding: _disableColumnHiding,
    ...rest
  } = props
  const localApiRef = useGridApiRef()
  const apiRef = useMemo(
    () => propApiRef ?? localApiRef,
    [propApiRef, localApiRef],
  )

  const defaultVisibleColumns = useMemo(() => {
    const visibleColumns = [
      '__check__',
      'chevron',
      'hover-actions',
      ...(pinFirstColumn && columns[0]?.field ? [columns[0]?.field] : []),
      ...(props.alwaysVisibleColumns ?? []),
    ]
    return visibleColumns
  }, [props.alwaysVisibleColumns, columns, pinFirstColumn])

  const getTogglableColumns = (columns: GridColDef[]): string[] => {
    return columns
      .filter(
        (column) =>
          !defaultVisibleColumns.includes(column.field) && column.hideable,
      )
      .map((column) => column.field)
  }

  const columnsTyped: Array<GridColDef<R>> = useMemo(() => {
    const hideableFromAlwaysVisibleColumns = (
      field: string,
    ): boolean | undefined => {
      return !defaultVisibleColumns.includes(field)
    }

    return columns.map((column) => ({
      ...column,
      renderHeader: column.renderHeader ?? renderHeader,
      renderCell: column.renderCell ?? renderCell,
      hideable:
        column.hideable ?? hideableFromAlwaysVisibleColumns(column.field),
    }))
  }, [columns, defaultVisibleColumns, renderHeader, renderCell])

  const columnVisibilityModelOverride = useMemo(() => {
    const visibilityOverride =
      defaultVisibleColumns.reduce<GridColumnVisibilityModel>((acc, field) => {
        acc[field] = true
        return acc
      }, {})

    return { ...columnVisibilityModel, ...visibilityOverride }
  }, [columnVisibilityModel, defaultVisibleColumns])

  const columnsState = useColumnsState(
    apiRef,
    columnsTyped,
    orderedColumnFields,
  )

  useEffect(() => {
    onRegisterResetColumnOrder?.(columnsState.resetColumnOrder)
  }, [onRegisterResetColumnOrder, columnsState.resetColumnOrder])

  const handleColumnOrderChange = useCallback<
    NonNullable<DataGridProProps['onColumnOrderChange']>
  >(() => {
    columnsState.onColumnOrderChange()
    onUserColumnOrderChange?.()
  }, [columnsState.onColumnOrderChange, onUserColumnOrderChange])

  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const main = root.querySelector<HTMLElement>('.MuiDataGrid-main')
    const scroller = root.querySelector<HTMLElement>(
      '.MuiDataGrid-virtualScroller',
    )
    if (!main || !scroller) return

    const compute = () => {
      const style = getComputedStyle(main)

      const leftPinned =
        parseFloat(style.getPropertyValue('--DataGrid-leftPinnedWidth')) || 0
      const rightPinned =
        parseFloat(style.getPropertyValue('--DataGrid-rightPinnedWidth')) || 0

      // Raw scrollable range the browser exposes
      const fullMax = Math.max(
        0,
        Math.round(scroller.scrollWidth - scroller.clientWidth),
      )

      // Effective scrollable range of the CONTENT (excluding pinned on both sides)
      const maxEffective = Math.max(
        0,
        Math.round(fullMax - leftPinned - rightPinned),
      )

      // guard against sub-pixel/retina fuzz
      const EPS = (window.devicePixelRatio || 1) > 1 ? 2 : 1

      // If there is no effective overflow, hide both fades
      if (maxEffective <= EPS) {
        main.setAttribute('data-left-fade', '0')
        main.setAttribute('data-right-fade', '0')
        return
      }

      const sl = Math.round(scroller.scrollLeft)

      // At start when we're within EPS of 0
      const atStart = sl <= EPS

      // At end when the remaining EFFECTIVE scroll (excluding right pinned) is within EPS
      // i.e., we've scrolled to fullMax - rightPinned
      const atEnd = fullMax - rightPinned - sl <= EPS

      main.setAttribute('data-left-fade', atStart ? '0' : '1')
      main.setAttribute('data-right-fade', atEnd ? '0' : '1')
    }

    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        compute()
        ticking = false
      })
    }

    // run after layout settles
    requestAnimationFrame(() => {
      compute()
      requestAnimationFrame(compute)
    })

    scroller.addEventListener('scroll', onScroll, { passive: true })

    // update when width/columns change
    const ro = new ResizeObserver(() => compute())
    ro.observe(scroller)
    const content = root.querySelector('.MuiDataGrid-virtualScrollerContent')
    if (content) ro.observe(content as Element)

    // update on window resize too
    const onWinResize = () => requestAnimationFrame(compute)
    window.addEventListener('resize', onWinResize)

    return () => {
      scroller.removeEventListener('scroll', onScroll)
      ro.disconnect()
      window.removeEventListener('resize', onWinResize)
    }
  }, [])
  useTransferVerticalScroll(rootRef, '.MuiDataGrid-virtualScroller')

  return (
    <div
      ref={rootRef}
      css={css`
        width: 100%;
        height: ${(props.height || 0) - DATA_GRID_HEIGHT_OFFSET}px;
      `}
    >
      <DataGridPro
        rowBuffer={props.rowBuffer ?? 20}
        columnBuffer={props.columnBuffer ?? 4}
        apiRef={apiRef}
        // overridden props, should be Omitted from extended type
        localeText={
          locale === ExtendLocales.frCA
            ? frFR?.components.MuiDataGrid.defaultProps.localeText
            : undefined
        }
        loading={props?.infiniteScrollProps?.loading}
        onRowsScrollEnd={handleOnRowsScrollEnd}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        editMode={editMode}
        sortingMode={'server'}
        // forwarded props with custom behaviour
        columns={columnsState.columns}
        onColumnWidthChange={columnsState.onColumnWidthChange}
        onColumnOrderChange={handleColumnOrderChange}
        getRowHeight={
          (getRowHeight ?? props.rowHeight)
            ? undefined
            : (): GridRowHeightReturnValue => 'auto'
        }
        columnHeaderHeight={DATA_GRID_HEADER_HEIGHT}
        slots={{
          loadingOverlay: loadingImage,
          columnMenu: customColumnMenu,
          ...slots,
        }}
        slotProps={{
          columnsManagement: {
            getTogglableColumns,
            disableShowHideToggle: true,
          },
          ...slotProps,
        }}
        sx={{
          '--DataGrid-rowBorderColor': base_colorContentBorder,
          borderRadius: 0,
          borderStyle: 'unset',
          borderTop: `1px solid ${headerTopBorderColor}`,
          // disable cell selection style
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          // disable cell selection style when editing or clicked
          '.MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within': {
            outline: 'none',
          },
          '.MuiDataGrid-cell.MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          // row styling
          // selected
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: table_tableRowSelected,
          },
          '& .MuiDataGrid-virtualScroller': {
            willChange: 'transform',
            transform: 'translateZ(0)',
            contain: 'layout paint',
            backfaceVisibility: 'hidden',
            overscrollBehaviorY: 'contain',
            overscrollBehaviorX: 'none',
          },
          '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: table_tableRowSelected,
            },
          '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft':
            {
              backgroundColor: table_tableRowSelected,
            },
          '--hover-color': `${table_tableRowHover}`,
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'var(--hover-color)',
            cursor: props.onRowClick ? 'pointer' : 'default',
          },
          // Pinned cells paint their own background (so content doesn't show
          // through on horizontal scroll), which otherwise stays on the default
          // surface while the rest of the row picks up the hover color. Repaint
          // the pinned first column / checkbox / right-pinned cells on hover so
          // an unselected row hovers as one. The selected variants below take
          // precedence for selected rows.
          '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row:not(.Mui-selected):hover .MuiDataGrid-cell--pinnedLeft':
            {
              backgroundColor: 'var(--hover-color)',
            },
          '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row:not(.Mui-selected):hover .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: 'var(--hover-color)',
            },
          // selected & hovered
          '& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell': {
            backgroundColor: table_tableRowSelectedHover,
          },
          '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft':
            {
              backgroundColor: table_tableRowSelectedHover,
            },
          '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: table_tableRowSelectedHover,
            },
          // editing row
          '.MuiDataGrid-row--editing': {
            boxShadow: 'none',
          },
          // defining a class to hide right border on column header
          '& .hideRightSeparator > .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-columnHeaderCheckbox > .MuiDataGrid-columnSeparator':
            {
              display: 'none',
            },
          // disable cell overflow to support tooltips
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            padding: 0,
          },
          '.MuiDataGrid-cell.MuiDataGrid-cell--editing': {
            display: 'flex',
            alignItems: 'center',
            boxShadow: 'none',
            padding: 0,
          },
          // column header bg color
          '.MuiDataGrid-columnHeader': {
            padding: '0 16px',
            backgroundColor: headerBgColor,
            borderLeftStyle: 'unset',
          },
          // remove cursor pointer and blue outline on column headers
          '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus': {
            outline: 'none',
            cursor: 'unset',
          },
          // keep column resize indicator even for pinned columns
          '.MuiDataGrid-columnHeader--withRightBorder': {
            borderRightStyle: 'unset',
            '& .MuiDataGrid-columnSeparator': {
              opacity: 1,
              transform: 'translateX(-1px)',
            },
          },
          '.MuiDataGrid-columnHeader--withLeftBorder': {
            borderLeftStyle: 'unset',
            '& .MuiDataGrid-columnSeparator': {
              opacity: 1,
              transform: 'translateX(1px)',
            },
          },
          '.MuiDataGrid-columnHeader--pinnedLeft': {
            zIndex: 31,
          },
          // style resets for columns
          '.MuiDataGrid-columnHeaderTitleContainer': {
            width: '100%',
          },
          '.MuiDataGrid-columnHeaderTitleContainerContent': {
            width: '100%',
          },
          '.MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainerContent':
            {
              justifyContent: 'center',
            },
          '.MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox': {
            padding: 0,
          },

          // prevent scrollbar from showing over flyouts
          '& .MuiDataGrid-scrollbar': {
            zIndex: 1,
          },
          // show action buttons on hover (add 'hover-actions' to the affected column's cellClassName property)
          '.MuiDataGrid-row': {
            '.hover-actions': {
              maxWidth: 0,
              '& > *': {
                opacity: 0,
                pointerEvents: 'none',
              },
              backgroundColor: 'transparent',
              borderLeft: 'none',
              borderRight: `1px solid ${base_colorContentBorder}`,
              overflow: 'visible',

              right: '0px',
              justifyContent: 'right',
            },
            '&:hover > .hover-actions > *': {
              pointerEvents: 'all',
              opacity: 1,
            },
          },
          // hide column header
          '.hover-actions-header': {
            pointerEvents: 'none',
            opacity: 0,
            maxWidth: 0,
            padding: 0,
          },
          // column header row
          '.MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
            backgroundColor: headerBgColor,
            '--DataGrid-t-header-background-base': headerBgColor,
          },
          // overrides for hover menu to rightAlign header text properly
          '.MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
            marginRight: 0,
          },
          '.MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': {
            marginRight: `-${spacing(2)}`,
          },
          position: 'relative',
          // make the main container a positioning context
          '& .MuiDataGrid-main': { position: 'relative', zIndex: 0 },

          // LEFT fade (hidden by default)
          '& .MuiDataGrid-main::before': {
            content: '""',
            position: 'absolute',
            top: 'var(--DataGrid-headerHeight, 44px)',
            bottom: 0,
            left: 'var(--DataGrid-leftPinnedWidth, 0px)',
            width: '10px',
            pointerEvents: 'none',
            zIndex: 2,
            background: `
              linear-gradient(
                to right,
                rgba(0,0,0,0.10) 0px,
                rgba(0,0,0,0.06) 3px,
                rgba(0,0,0,0.03) 6px,
                rgba(0,0,0,0.015) 8px,
                rgba(0,0,0,0) 10px
              ) 0 0 / 16px 100% no-repeat
            `,
            opacity: 0,
            transition: 'opacity 150ms ease-out',
          },

          // RIGHT fade (hidden by default)
          '& .MuiDataGrid-main::after': {
            content: '""',
            position: 'absolute',
            top: 'var(--DataGrid-headerHeight, 44px)',
            bottom: 0,
            right: 'var(--DataGrid-rightPinnedWidth, 0px)',
            width: '10px',
            pointerEvents: 'none',
            zIndex: 2,
            background: `
              linear-gradient(
                to left,
                rgba(0,0,0,0.10) 0px,
                rgba(0,0,0,0.06) 3px,
                rgba(0,0,0,0.03) 6px,
                rgba(0,0,0,0.015) 8px,
                rgba(0,0,0,0) 10px
              ) 100% 0 / 16px 100% no-repeat
            `,
            opacity: 0,
            transition: 'opacity 150ms ease-out',
          },

          // toggle visibility based on scroll position
          '& .MuiDataGrid-main[data-left-fade="1"]::before': { opacity: 1 },
          '& .MuiDataGrid-main[data-right-fade="1"]::after': { opacity: 1 },
          // BEFORE the consumer's `sx`, deliberately: the variant is a default
          // a call site may still override, not a rule imposed on it. Both are
          // shallow spreads, so a key in either REPLACES the rule above it
          // rather than extending it — which is why `.MuiDataGrid-cell` above
          // restates the declarations it takes over.
          ...kickerSx,
          ...sx,
        }}
        columnVisibilityModel={columnVisibilityModelOverride}
        initialState={{
          pinnedColumns: {
            right: [
              'hover-actions',
              ...(initialState?.pinnedColumns?.right ?? []),
              'chevron',
            ],
            left: [
              '__check__',
              ...(pinFirstColumn && columns[0]?.field
                ? [columns[0]?.field]
                : []),
              ...(initialState?.pinnedColumns?.left ?? []),
            ],
          },
        }}
        rowSelectionPropagation={{ parents: false, descendants: false }}
        // props with default behaviour
        {...rest}
      />
    </div>
  )
}
export default DataGrid
